"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "useScrollDepthTracker", {
    enumerable: true,
    get: function() {
        return useScrollDepthTracker;
    }
});
const _react = require("react");
const _hooks = require("./hooks");
function useScrollDepthTracker(options = {}) {
    const { percentages = [
        10,
        25,
        50,
        75,
        90
    ], sectionSelectors = [
        'section',
        '[data-scroll-section]'
    ], trackSections = true, trackPercentages = true, flushInterval = 1000 } = options;
    const analytics = (0, _hooks.useAnalytics)();
    const [isEnabled, setIsEnabled] = (0, _react.useState)(false);
    const observersRef = (0, _react.useRef)([]);
    const flushIntervalRef = (0, _react.useRef)();
    const hasUserScrolledRef = (0, _react.useRef)(false);
    const initialViewportSectionsRef = (0, _react.useRef)(new Set());
    const firedPercentagesRef = (0, _react.useRef)(new Set());
    const firedSectionsRef = (0, _react.useRef)(new Set());
    const getDocumentHeight = (0, _react.useCallback)(()=>{
        return Math.max(document.body.scrollHeight, document.body.offsetHeight, document.documentElement.clientHeight, document.documentElement.scrollHeight, document.documentElement.offsetHeight);
    }, []);
    const logPercentageScrollObserved = (0, _react.useCallback)((offset, description)=>{
        if (!trackPercentages || !hasUserScrolledRef.current) return;
        const documentHeight = getDocumentHeight();
        const percentage = offset / documentHeight * 100;
        percentages.forEach((threshold)=>{
            if (percentage >= threshold && !firedPercentagesRef.current.has(threshold)) {
                analytics.track('scrolled', {
                    category: 'Page',
                    label: `percent_${threshold}`,
                    description
                });
                firedPercentagesRef.current.add(threshold);
            }
        });
    }, [
        analytics,
        percentages,
        trackPercentages,
        getDocumentHeight
    ]);
    const handleSectionIntersection = (0, _react.useCallback)((index, identifier)=>(entries)=>{
            const entry = entries.length > 0 ? entries[0] : null;
            if (!(entry === null || entry === void 0 ? void 0 : entry.isIntersecting)) return;
            const section = entry.target;
            if (initialViewportSectionsRef.current.has(section) && !hasUserScrolledRef.current) {
                return;
            }
            if (firedSectionsRef.current.has(index)) {
                return;
            }
            const rect = section.getBoundingClientRect();
            const offset = rect.top + window.scrollY;
            logPercentageScrollObserved(offset, String(index));
            if (trackSections) {
                analytics.track('scrolled', {
                    category: 'Section',
                    label: `index_${index}`,
                    description: identifier || `section_${index}`
                });
                firedSectionsRef.current.add(index);
            }
        }, [
        analytics,
        logPercentageScrollObserved,
        trackSections
    ]);
    const gatherSections = (0, _react.useCallback)(()=>{
        const selector = sectionSelectors.join(', ');
        const elements = Array.from(document.querySelectorAll(selector));
        return elements.filter((element)=>{
            const styles = window.getComputedStyle(element);
            if (styles.display === 'none' || styles.visibility === 'hidden') {
                return false;
            }
            let parent = element.parentElement;
            while(parent){
                const parentStyles = window.getComputedStyle(parent);
                if (parentStyles.display === 'none' || parentStyles.visibility === 'hidden') {
                    return false;
                }
                parent = parent.parentElement;
            }
            return true;
        });
    }, [
        sectionSelectors
    ]);
    const createObservers = (0, _react.useCallback)(()=>{
        const sections = gatherSections();
        initialViewportSectionsRef.current.clear();
        sections.forEach((section)=>{
            const rect = section.getBoundingClientRect();
            const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
            if (isVisible) {
                initialViewportSectionsRef.current.add(section);
            }
        });
        sections.forEach((section, index)=>{
            var _heading_textContent;
            const headingSelector = 'h1, h2, h3, h4, h5, h6';
            const heading = section.querySelector(headingSelector);
            const identifier = section.getAttribute('data-scroll-id') || section.getAttribute('id') || (heading === null || heading === void 0 ? void 0 : (_heading_textContent = heading.textContent) === null || _heading_textContent === void 0 ? void 0 : _heading_textContent.slice(0, 64)) || `section_${index}`;
            const observer = new IntersectionObserver(handleSectionIntersection(index, identifier), {
                threshold: 0.01,
                rootMargin: '0px 0px 0px 0px'
            });
            observer.observe(section);
            observersRef.current.push(observer);
        });
    }, [
        gatherSections,
        handleSectionIntersection
    ]);
    const handleFlush = (0, _react.useCallback)(()=>{
        if (hasUserScrolledRef.current) {
            logPercentageScrollObserved(window.scrollY, 'flush');
        }
    }, [
        logPercentageScrollObserved
    ]);
    const handleScroll = (0, _react.useCallback)(()=>{
        hasUserScrolledRef.current = true;
    }, []);
    const handleResize = (0, _react.useCallback)(()=>{
        setIsEnabled(false);
        hasUserScrolledRef.current = false;
        firedPercentagesRef.current.clear();
        firedSectionsRef.current.clear();
        observersRef.current.forEach((observer)=>observer.disconnect());
        observersRef.current = [];
        setTimeout(()=>setIsEnabled(true), 100);
    }, []);
    (0, _react.useEffect)(()=>{
        if (window.scrollY > window.innerHeight) {
            return;
        }
        setIsEnabled(true);
        return ()=>{
            setIsEnabled(false);
        };
    }, []);
    (0, _react.useEffect)(()=>{
        if (!isEnabled) return;
        createObservers();
        if (flushInterval > 0) {
            flushIntervalRef.current = setInterval(handleFlush, flushInterval);
        }
        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleScroll, {
            passive: true
        });
        return ()=>{
            observersRef.current.forEach((observer)=>observer.disconnect());
            observersRef.current = [];
            if (flushIntervalRef.current) {
                clearInterval(flushIntervalRef.current);
            }
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [
        isEnabled,
        createObservers,
        handleFlush,
        handleResize,
        handleScroll,
        flushInterval
    ]);
    return {
        isEnabled,
        reset: ()=>{
            hasUserScrolledRef.current = false;
            firedPercentagesRef.current.clear();
            firedSectionsRef.current.clear();
            setIsEnabled(false);
            setTimeout(()=>setIsEnabled(true), 100);
        }
    };
}

//# sourceMappingURL=useScrollDepthTracker.js.map